import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearTargetDayForecasts,
  fetchTargetDayForecasts,
  selectModel,
  selectTargetDayForecasts,
  selectTargetDayForecastsLoading,
} from '../../../slices/marketSlice';
import {
  clearSessionRawData,
  fetchRawData,
  selectSessionRawData,
} from '../../../slices/dataSlice';
import {
  deriveGateOptions,
  deriveHorizonOptions,
  gateHorizonKey,
  pickDefaultGate,
  pickDefaultHorizon,
} from './sessionSelectors';

// Selection state machine for the session detail: day → horizon → gate →
// challenge. Applies the default rule (Intraday-else-latest) when the day
// changes, and fetches the selected challenge's forecasts + observed. Pure
// selection logic lives in sessionSelectors (tested there).
export const useSessionDetail = ({ targetDay, gates = [], resourceId }) => {
  const dispatch = useDispatch();
  const model = useSelector(selectModel);
  const targetDayForecasts = useSelector(selectTargetDayForecasts);
  const targetDayForecastsLoading = useSelector(selectTargetDayForecastsLoading);
  const sessionRawData = useSelector(selectSessionRawData);

  const [horizonKey, setHorizonKey] = useState(null);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);

  const horizonOptions = deriveHorizonOptions(gates);
  const gateOptions = deriveGateOptions(gates, horizonKey);
  const selectedGate =
    gateOptions.find((g) => g.challenge_id === selectedChallengeId) || gateOptions[0] || null;

  // Stable signature so defaults only re-apply when the day's gates change.
  const gateSignature = gates.map((g) => g.challenge_id).join(',');

  useEffect(() => {
    const key = pickDefaultHorizon(gates)?.key || null;
    setHorizonKey(key);
    setSelectedChallengeId(pickDefaultGate(gates, key)?.challenge_id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateSignature]);

  const onHorizonChange = useCallback(
    (key) => {
      setHorizonKey(key);
      setSelectedChallengeId(pickDefaultGate(gates, key)?.challenge_id || null);
    },
    [gates]
  );

  // Select a gate directly (from the day panel): jump to its horizon and gate
  // in one step, since the gate may belong to a different horizon than current.
  const selectGate = useCallback(
    (challengeId) => {
      const gate = gates.find((g) => g.challenge_id === challengeId);
      if (!gate) return;
      setHorizonKey(gateHorizonKey(gate));
      setSelectedChallengeId(challengeId);
    },
    [gates]
  );

  const challengeId = selectedGate?.challenge_id || null;
  const challengeStart = selectedGate?.start_datetime || null;
  const challengeEnd = selectedGate?.end_datetime || null;

  useEffect(() => {
    if (!resourceId || !targetDay || !challengeId) return;
    dispatch(clearTargetDayForecasts());
    dispatch(clearSessionRawData());
    dispatch(fetchTargetDayForecasts({ resourceId, targetDay, challengeId, model }));
    dispatch(
      fetchRawData({
        startDate: challengeStart || `${targetDay}T00:00:00Z`,
        endDate: challengeEnd || `${targetDay}T23:45:00Z`,
        resourceId,
        sessionOnly: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, resourceId, targetDay, challengeId, model]);

  return {
    horizonKey,
    horizonOptions,
    gateOptions,
    selectedChallengeId,
    selectedGate,
    setHorizonKey: onHorizonChange,
    setSelectedChallengeId,
    selectGate,
    targetDayForecasts,
    targetDayForecastsLoading,
    sessionRawData,
  };
};
